import { useEffect, useState } from 'react';
import type { QuietHours, QuietWindow } from '@keetcheck/shared';
import { theme } from '../theme.js';
import { useQuietHours, useSetQuietHours } from '../lib/queries.js';
import { Switch } from './NotificationsCard.js';

const ROWS: { key: 'weekdays' | 'weekends' | 'holidays'; label: string; hint: string }[] = [
  { key: 'weekdays', label: 'Weekdays', hint: 'Starting Mon–Fri' },
  { key: 'weekends', label: 'Weekends', hint: 'Starting Sat–Sun' },
  { key: 'holidays', label: 'Public holidays', hint: 'Your unit’s holiday list' },
];

/**
 * Profile card: times when notifications wait. What arrives then is sent as
 * one catch-up when the quiet time ends; urgent ones can still come through.
 */
export function QuietHoursCard() {
  const { data } = useQuietHours();
  const save = useSetQuietHours();
  const [draft, setDraft] = useState<QuietHours | null>(null);
  useEffect(() => { if (data && !draft) setDraft(data.settings); }, [data, draft]);
  if (!data || !draft) return null;

  const dirty = JSON.stringify(draft) !== JSON.stringify(data.settings);
  const anyOn = draft.weekdays.on || draft.weekends.on || draft.holidays.on;
  const set = (key: keyof QuietHours, w: Partial<QuietWindow>) =>
    setDraft({ ...draft, [key]: { ...(draft[key] as QuietWindow), ...w } });

  return (
    <div style={{
      margin: '12px 16px', padding: '12px 14px', borderRadius: 12,
      background: theme.surface, border: `1px solid ${theme.border}`, fontFamily: theme.font,
    }}>
      <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Quiet hours</div>
      <div style={{ fontSize: 12, color: data.pausedUntil ? theme.status.warn.fg : theme.textMuted, lineHeight: 1.4, fontWeight: data.pausedUntil ? 600 : 400 }}>
        {data.pausedUntil
          ? `Paused until ${fmtUntil(data.pausedUntil)}${data.held ? ` · ${data.held} waiting` : ''}`
          : 'Notifications wait during these times and come as one catch-up when they end.'}
      </div>

      {ROWS.map(({ key, label, hint }) => {
        const w = draft[key];
        return (
          <div key={key} style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>{label}</div>
                <div style={{ fontSize: 12, color: theme.textMuted }}>{w.on ? (w.allDay ? 'All day' : `${w.from} – ${w.until}${w.until <= w.from ? ' next day' : ''}`) : hint}</div>
              </div>
              <Switch on={w.on} label={`Quiet hours ${label.toLowerCase()}`} busy={false} onClick={() => set(key, { on: !w.on })} />
            </div>
            {w.on && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
                {!w.allDay && (
                  <>
                    <input type="time" value={w.from} aria-label={`${label} from`} onChange={(e) => e.target.value && set(key, { from: e.target.value })} style={time} />
                    <span style={{ fontSize: 13, color: theme.textMuted }}>to</span>
                    <input type="time" value={w.until} aria-label={`${label} until`} onChange={(e) => e.target.value && set(key, { until: e.target.value })} style={time} />
                  </>
                )}
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: theme.textDim, cursor: 'pointer', marginLeft: w.allDay ? 0 : 'auto' }}>
                  <input type="checkbox" checked={w.allDay} onChange={(e) => set(key, { allDay: e.target.checked })} /> All day
                </label>
              </div>
            )}
          </div>
        );
      })}

      {anyOn && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, paddingTop: 12, borderTop: `1px solid ${theme.border}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: theme.text }}>Let urgent ones through</div>
            <div style={{ fontSize: 12, color: theme.textMuted, lineHeight: 1.4 }}>
              A vehicle off run, your crew short, or someone asking you directly to cover or swap in the next 12 hours.
            </div>
          </div>
          <Switch on={draft.urgent} label="Let urgent notifications through" busy={false} onClick={() => setDraft({ ...draft, urgent: !draft.urgent })} />
        </div>
      )}

      {save.isError && <div style={{ fontSize: 12, color: theme.status.danger.fg, marginTop: 8 }}>{(save.error as Error).message}</div>}
      {dirty && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button onClick={() => setDraft(data.settings)} style={{ ...btn, flex: 1, background: theme.surface2, color: theme.textDim, border: `1px solid ${theme.border}` }}>Cancel</button>
          <button onClick={() => save.mutate(draft)} disabled={save.isPending} style={{ ...btn, flex: 2, background: theme.accent, color: theme.accentInk }}>
            {save.isPending ? 'Saving…' : 'Save quiet hours'}
          </button>
        </div>
      )}
      <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 10 }}>
        Applies to all your devices and units. Email isn’t held. Catch-ups go out on the hour, a few minutes after your quiet time ends.
      </div>
    </div>
  );
}

function fmtUntil(iso: string) {
  const d = new Date(iso);
  const today = new Date().toDateString() === d.toDateString();
  return d.toLocaleString(undefined, today ? { hour: 'numeric', minute: '2-digit' } : { weekday: 'short', hour: 'numeric', minute: '2-digit' });
}

const time: React.CSSProperties = {
  padding: '7px 10px', fontSize: 14, borderRadius: 8, border: `1px solid ${theme.border}`,
  background: theme.surface, color: theme.text, fontFamily: theme.font,
};
const btn: React.CSSProperties = {
  padding: '9px 0', borderRadius: 8, border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: theme.font,
};
